/**
 * Reads from the mock, writes to both — the real service shadowing the mock.
 *
 * ## Why this shape and not a switch
 *
 * The service exists and is reachable, but its storage is an in-memory map: a Lambda cold start
 * empties it and two instances do not share it. So pointing the editor at it would make the canvas
 * lose its tree at random. Pointing the editor at the mock and sending the writes to BOTH gets the
 * one thing that is actually unknown — do the requests authenticate, does the payload map, do the
 * conditional writes behave — without betting the UI on storage that cannot hold.
 *
 * Three rules, and each is load-bearing:
 *
 *  1. **The mock's result is what the caller gets.** Returning the service's would hand the editor
 *     an `updatedAt` the mock has never seen, and the next conditional write would fail against
 *     its own store. The mock is the source of truth for the session.
 *  2. **A shadow failure never reaches the caller.** That is the whole "does not affect the demo"
 *     guarantee: a 403, an unparsed field, a Lambda that is not deployed — the editor does not
 *     notice. It is logged, loudly, because an unobserved shadow write is worth nothing.
 *  3. **The shadow write runs AFTER the mock succeeds.** If the mock refuses — a stale timestamp,
 *     a lost race — the operation did not happen, and sending it to the service anyway would
 *     populate it with writes the user was told were rejected.
 *
 * ## What it cannot tell us
 *
 * Whether anything was stored. Every shadow request lands on an instance with its own empty map,
 * so a `createDraft` followed by a `putDraft` may hit different instances and the second will
 * report "no draft" — correctly. That is a property of the service's current storage, not a bug
 * here, and it is why the DAO is the next piece. Read the log for "did it authenticate and map",
 * not for "is the data there".
 */
import type {
  DecisionTreeApi,
  DecisionTreeDraftData,
  DecisionTreeEditingContext,
  DecisionTreeVersionData,
  DraftDisposition,
} from './treeApiTypes';

type Shadowed = 'createDraft' | 'putDraft' | 'deleteDraft' | 'commitVersion' | 'publishVersion';

export class DualWriteDecisionTreeApi implements DecisionTreeApi {
  constructor(
    private readonly mock: DecisionTreeApi,
    private readonly real: DecisionTreeApi,
  ) {}

  /* ── Reads: mock only ───────────────────────────────────────────────────── */

  getDraft(input: { treeId: string }) {
    return this.mock.getDraft(input);
  }

  listEditors(input: { treeId: string }) {
    return this.mock.listEditors(input);
  }

  getVersion(input: { treeId: string; version: number }) {
    return this.mock.getVersion(input);
  }

  listVersions(input: { treeId: string; maxResults?: number; nextToken?: string }) {
    return this.mock.listVersions(input);
  }

  /* ── Writes: mock first, then the service ───────────────────────────────── */

  async createDraft(input: {
    treeId: string;
    baseVersion?: number;
    clientToken?: string;
  }): Promise<{ draft: DecisionTreeDraftData; context: DecisionTreeEditingContext }> {
    const result = await this.mock.createDraft(input);
    this.shadow('createDraft', () => this.real.createDraft(input));
    return result;
  }

  async putDraft(input: {
    treeId: string;
    document: string;
    schemaVersion: number;
    expectedUpdatedAt: string;
  }): Promise<{ draft: DecisionTreeDraftData; context: DecisionTreeEditingContext }> {
    const result = await this.mock.putDraft(input);
    this.shadow('putDraft', () => this.real.putDraft(input));
    return result;
  }

  async deleteDraft(input: { treeId: string; expectedUpdatedAt: string }): Promise<void> {
    await this.mock.deleteDraft(input);
    this.shadow('deleteDraft', () => this.real.deleteDraft(input));
  }

  async commitVersion(input: {
    treeId: string;
    expectedDraftUpdatedAt: string;
    documentSha256: string;
    baseVersion: number;
    note: string;
    mergedFrom?: number[];
    clientToken?: string;
  }): Promise<{ version: DecisionTreeVersionData; draft: DraftDisposition }> {
    const result = await this.mock.commitVersion(input);
    this.shadow('commitVersion', () => this.real.commitVersion(input));
    return result;
  }

  async publishVersion(input: {
    treeId: string;
    version: number;
    expectedUpdatedAt: string;
  }): Promise<{ version: DecisionTreeVersionData }> {
    const result = await this.mock.publishVersion(input);
    this.shadow('publishVersion', () => this.real.publishVersion(input));
    return result;
  }

  /**
   * Fire the shadow write and swallow whatever it does.
   *
   * Not awaited: the editor's autosave is on a timer and a slow service would make the canvas
   * feel slow for a request nobody is waiting on. `void` plus a `.catch` rather than try/catch
   * because the point is precisely that this promise is never joined.
   */
  private shadow(op: Shadowed, run: () => Promise<unknown>): void {
    const t0 = performance.now();
    void run().then(
      (out) => {
        // eslint-disable-next-line no-console
        console.info(
          `%c[CPA 影子写]%c ${op} → 成功 · ${Math.round(performance.now() - t0)}ms`,
          'background:#00802f;color:#fff;padding:1px 6px;border-radius:3px;font-weight:600',
          'color:#5f6b7a',
          out,
        );
      },
      (e: unknown) => {
        // eslint-disable-next-line no-console
        console.warn(
          `%c[CPA 影子写]%c ${op} → 失败（不影响 UI，UI 用的是 mock 的结果） · ` +
            `${Math.round(performance.now() - t0)}ms`,
          'background:#8a6116;color:#fff;padding:1px 6px;border-radius:3px;font-weight:600',
          'color:#5f6b7a',
          e,
        );
      },
    );
  }
}
